'use client';

import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';

/**
 * OrgContext provides organization context to components that need to scope
 * operations (like search) to a specific organization.
 *
 * Architecture:
 * - OrgContextProvider wraps the root layout with a shared state
 * - OrgContextSetter is used by child layouts (e.g., org vault layout) to update the state
 * - Components use useOrgContext() to read the current org
 *
 * This pattern is necessary because CommandCenterProvider/SearchProvider are in the
 * root layout ABOVE {children}, so nested layouts can't directly provide context to them.
 * Instead, child layouts "push" the org info up via the setter.
 */

interface OrgContextValue {
  orgId: string | undefined;
  orgSlug: string | undefined;
}

interface OrgContextProviderValue extends OrgContextValue {
  setOrg: (org: OrgContextValue) => void;
}

const OrgContext = createContext<OrgContextProviderValue>({
  orgId: undefined,
  orgSlug: undefined,
  setOrg: () => {},
});

export function OrgContextProvider({ children }: { children: ReactNode }) {
  const [org, setOrgState] = useState<OrgContextValue>({
    orgId: undefined,
    orgSlug: undefined,
  });

  const setOrg = useCallback((newOrg: OrgContextValue) => {
    setOrgState((prev) => {
      // Only update if values changed to avoid unnecessary re-renders
      if (prev.orgId === newOrg.orgId && prev.orgSlug === newOrg.orgSlug) {
        return prev;
      }
      return newOrg;
    });
  }, []);

  return <OrgContext.Provider value={{ ...org, setOrg }}>{children}</OrgContext.Provider>;
}

/**
 * Hook to get the current organization context.
 * Returns undefined values when not within an org-scoped route.
 */
export function useOrgContext(): OrgContextValue {
  const { orgId, orgSlug } = useContext(OrgContext);
  return { orgId, orgSlug };
}

/**
 * Hook to get the setter for updating org context.
 * Used by OrgContextSetter component in child layouts.
 */
export function useSetOrg() {
  const { setOrg } = useContext(OrgContext);
  return setOrg;
}
